import { isEqual } from 'lodash';

import {
  AugmentedCreatePresentationBundleInput,
  AugmentedUpdateClientPresentationV2Input,
  AugmentedUpdatePresentationBundleInput,
  UpdateClientPresentationV2Input,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { formatDateToMMDDYY } from '@/utils/formatting/dates';

import {
  mapBundleToUpdateInput,
  NEW_BUNDLE_ID_SENTINEL,
} from '../../ClientPresentationDesigner.utils';
import {
  BuildSaveInputArgs,
  PresentationSaveContext,
  PresentationSaveStrategy,
  ResolveContextMapKeyArgs,
} from '../presentationSave.types';
import { reconcilePresentation } from '../reconcilePresentation';
import { canWriteBundle, diffBundle } from './diffBundle';

/**
 * Presentation-level scalars. Bundles are deliberately absent — they are diffed
 * per kind, one stack layer at a time.
 */
const PRESENTATION_SCALARS = [
  'pageNumberToStartFrom',
  'showLegalDisclaimer',
  'showClientNamesInHeader',
  'showPageNumbers',
  'showPageNumbersOnCoverSlide',
] as const;

/**
 * Writes just the presentation scalars that changed. Notably absent:
 * `clearBundles`, so bundles, pages and configuration rows keep their server
 * identity.
 */
function buildPresentationInput({
  bundles,
  presentationConfiguration,
  title,
  context,
  previous,
}: BuildSaveInputArgs): AugmentedUpdateClientPresentationV2Input | null {
  const update: UpdateClientPresentationV2Input = {};

  if (title !== previous.title) {
    // The full write's placeholder, so clearing a title behaves the same here.
    update.name =
      title ?? `Untitled Presentation ${formatDateToMMDDYY(new Date())}`;
  }

  PRESENTATION_SCALARS.forEach((field) => {
    if (
      presentationConfiguration[field] !==
      previous.presentationConfiguration[field]
    ) {
      // Each scalar is independently typed, so narrow in a helper rather than
      // index the input with a union of value types.
      assignScalar(update, field, presentationConfiguration[field]);
    }
  });

  // Create and update indices both use final form order.
  const previousById = new Map(
    previous.bundles.map((bundle) => [bundle.id, bundle])
  );
  const nextIds = new Set(bundles.map((bundle) => bundle.id));

  // The form omits persisted indices, so write every index when the deck shape
  // changes.
  const survivingIdsBefore = previous.bundles
    .filter((bundle) => nextIds.has(bundle.id))
    .map((bundle) => bundle.id);
  const survivingIdsAfter = bundles
    .filter((bundle) => previousById.has(bundle.id))
    .map((bundle) => bundle.id);
  const deckShapeChanged =
    survivingIdsAfter.length !== bundles.length ||
    survivingIdsBefore.length !== previous.bundles.length ||
    !isEqual(survivingIdsBefore, survivingIdsAfter);
  const withBundles: AugmentedCreatePresentationBundleInput[] = [];
  const updateBundles: AugmentedUpdatePresentationBundleInput[] = [];

  bundles.forEach((bundle, index) => {
    const before = previousById.get(bundle.id);

    // Not in the baseline, so the server has never seen it. Built with the same
    // create input the full write uses, which is what carries its pages, its
    // configuration and a pending generation request.
    if (!before) {
      withBundles.push(
        mapBundleToUpdateInput(bundle, context.householdId, index)
      );
      return;
    }

    // An untouched bundle is only being moved, and a move is written from the
    // position rather than from the bundle's own contents, so it is never refused.
    if (!isEqual(before, bundle) && !canWriteBundle(before, bundle)) {
      diagnostics.error(
        'Presentation incremental save dropped unsupported bundle changes',
        new Error(`This save cannot express changes to ${bundle.type}`),
        {
          presentation_save_strategy: 'incremental',
          presentation_save_failure: 'unsupported_bundle_change',
          bundle_kind: bundle.type,
        }
      );
      // Its position is still safe to write: that is the generic differ's, not
      // the descriptor's.
      if (deckShapeChanged) {
        updateBundles.push({ id: bundle.id, update: { index } });
      }
      return;
    }

    const entry = diffBundle({
      previous: before,
      next: bundle,
      index,
      writeIndex: deckShapeChanged,
      context,
    });
    if (entry) updateBundles.push(entry);
  });

  if (
    Object.keys(update).length === 0 &&
    updateBundles.length === 0 &&
    withBundles.length === 0
  ) {
    return null;
  }

  return {
    id: context.presentationId,
    update,
    // Omitted rather than sent empty, so a payload says only what it means to
    // write and a no-op stays a no-op.
    ...(withBundles.length > 0 ? { withBundles } : {}),
    ...(updateBundles.length > 0 ? { updateBundles } : {}),
  };
}

export function buildDeleteBundleInput(
  bundleId: string,
  context: PresentationSaveContext
): AugmentedUpdateClientPresentationV2Input {
  return {
    id: context.presentationId,
    update: { removeBundleIDs: [bundleId] },
  };
}

function assignScalar(
  update: UpdateClientPresentationV2Input,
  field: (typeof PRESENTATION_SCALARS)[number],
  value: number | boolean
): void {
  if (field === 'pageNumberToStartFrom') {
    update.pageNumberToStartFrom = Number(value);
    return;
  }
  update[field] = Boolean(value);
}

/**
 * Keys a response bundle's server-computed data by the id the form holds. An
 * incremental save keeps server identity, so the response names an existing
 * bundle directly — unlike the full write, where `clearBundles` mints new ids and
 * position is the only correlation left.
 *
 * Position covers one case: a bundle *this* save created still holds a sentinel,
 * so the response id is what the slides get keyed by. Anything else is a desync,
 * and is skipped rather than keyed onto whichever bundle sits at that index.
 */
function resolveContextMapKey({
  responseBundle,
  index,
  bundles,
}: ResolveContextMapKeyArgs): string | null {
  const known = bundles.find((bundle) => bundle.id === responseBundle.id);
  if (known) return known.id;

  if (bundles[index]?.id.startsWith(NEW_BUNDLE_ID_SENTINEL)) {
    return responseBundle.id;
  }

  diagnostics.warn(
    `Response bundle ${responseBundle.id} at index ${index} matches no form bundle`
  );
  return null;
}

/**
 * The incremental save, gated by `presentation_stable_save`. It supports every
 * bundle kind plus explicit creation, deletion and reordering. See T1-6071.
 */
export function presentationIncrementalSaveStrategy(): PresentationSaveStrategy {
  return {
    type: 'incremental',
    buildDeleteBundleInput,
    buildPresentationInput,
    reconcilePresentation,
    resolveContextMapKey,
  };
}
